import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  BarChart2,
  Minus,
  Loader2,
  ClipboardList,
  Users,
  TrendingUp,
  CheckCircle2,
  XCircle,
  RefreshCw,
  GripVertical,
  MessageSquarePlus,
} from 'lucide-react';
import api from '../services/api';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
  CardFooter,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../components/ui/use-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  validation?: Record<string, any>;
}

interface PreChatForm {
  id: number;
  title: string;
  subtitle: string;
  fields: FormField[];
  channel_id?: number;
  is_enabled: boolean;
  is_required: boolean;
  show_message_box: boolean;
  logo_url?: string;
  primary_color: string;
  enable_business_hours_check: boolean;
  offline_message: string;
  total_submissions: number;
  conversion_rate: number;
  created_at: string;
}

interface FormSubmission {
  id: number;
  form_data: Record<string, any>;
  initial_message?: string;
  submitted_at: string;
  contact_id?: number;
  conversation_id?: number;
}

const FIELD_TYPES = [
  { value: 'text',     label: 'Text Input' },
  { value: 'email',    label: 'Email' },
  { value: 'tel',      label: 'Phone Number' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date Picker' },
  { value: 'time', label: 'Time Picker' },
  { value: 'datetime', label: 'Date & Time Picker' },
  { value: 'file', label: 'File Upload' },
  { value: 'rating', label: 'Star Rating' },
];

const DEFAULT_FORM_DATA = {
  title: "Let's get you connected!",
  subtitle: 'Please provide some details to help us assist you better.',
  fields: [] as FormField[],
  is_enabled: true,
  is_required: false,
  show_message_box: true,
  primary_color: '#1f93ff',
  enable_business_hours_check: false,
  offline_message: "We're currently offline. Please leave your details and we'll get back to you.",
};

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

// ─── Page ─────────────────────────────────────────────────────────────────────
const PreChatFormsPage: React.FC = () => {
  const [forms, setForms] = useState<PreChatForm[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [predefinedFields, setPredefinedFields] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [activeSubmissionFormTitle, setActiveSubmissionFormTitle] = useState<string>('');

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<PreChatForm | null>(null);
  const [formToDelete, setFormToDelete] = useState<PreChatForm | null>(null);

  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [newField, setNewField] = useState<FormField>({
    id: '', type: 'text', label: '', placeholder: '', required: false, options: [], validation: {},
  });

  const activeForms = forms.filter(f => f.is_enabled).length;
  const totalSubmissions = forms.reduce((sum, f) => sum + (f.total_submissions || 0), 0);
  const avgConversion = forms.length > 0
    ? Math.round(forms.reduce((sum, f) => sum + (f.conversion_rate || 0), 0) / forms.length)
    : 0;

  // ── Data ────────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [formsRes, fieldsRes] = await Promise.all([
        api.get('/pre-chat-forms/'),
        api.get('/pre-chat-forms/public/predefined-fields'),
      ]);
      setForms(formsRes.data || []);
      setPredefinedFields(fieldsRes.data.fields || {});
    } catch {
      toast({ title: 'Failed to load forms', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSaveForm = async () => {
    try {
      if (selectedForm) {
        await api.put(`/pre-chat-forms/${selectedForm.id}`, formData);
        toast({ title: 'Form updated successfully' });
      } else {
        await api.post('/pre-chat-forms/', formData);
        toast({ title: 'Form created successfully' });
      }
      setFormDialogOpen(false);
      setSelectedForm(null);
      resetForm();
      fetchData();
    } catch {
      toast({ title: 'Failed to save form', variant: 'destructive' });
    }
  };

  const handleEditForm = (form: PreChatForm) => {
    setSelectedForm(form);
    setFormData({
      title: form.title,
      subtitle: form.subtitle,
      fields: form.fields,
      is_enabled: form.is_enabled,
      is_required: form.is_required,
      show_message_box: form.show_message_box,
      primary_color: form.primary_color,
      enable_business_hours_check: form.enable_business_hours_check,
      offline_message: form.offline_message,
    });
    setFormDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return;
    try {
      await api.delete(`/pre-chat-forms/${formToDelete.id}`);
      toast({ title: 'Form deleted successfully' });
      fetchData();
    } catch {
      toast({ title: 'Failed to delete form', variant: 'destructive' });
    } finally {
      setFormToDelete(null);
    }
  };

  const handleAddField = () => {
    if (!newField.label || !newField.id) {
      toast({ title: 'Field ID and Label are required', variant: 'destructive' });
      return;
    }
    setFormData((prev) => ({ ...prev, fields: [...prev.fields, { ...newField }] }));
    setNewField({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [], validation: {} });
  };

  const handleRemoveField = (index: number) => {
    setFormData((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
  };

  const handleAddPredefinedField = (fieldKey: string) => {
    const field = predefinedFields[fieldKey];
    if (!field) return;
    setFormData((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          id: fieldKey,
          type: field.type,
          label: field.label,
          placeholder: field.placeholder || '',
          required: field.required || false,
          options: field.options || [],
          validation: field.validation || {},
        },
      ],
    }));
  };

  const fetchSubmissions = async (form: PreChatForm) => {
    try {
      const res = await api.get(`/pre-chat-forms/${form.id}/submissions`);
      setSubmissions(res.data.submissions || []);
      setActiveSubmissionFormTitle(form.title);
    } catch {
      toast({ title: 'Failed to load submissions', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setNewField({ id: '', type: 'text', label: '', placeholder: '', required: false, options: [], validation: {} });
  };

  const PreviewForm = ({ form }: { form: typeof formData }) => (
    <Card className="max-w-md mx-auto">
      <div className="px-4 py-3 text-white rounded-t-lg" style={{ backgroundColor: form.primary_color }}>
        <p className="font-semibold">{form.title}</p>
        {form.subtitle && <p className="text-sm text-white/80">{form.subtitle}</p>}
      </div>
      <CardContent className="space-y-3 p-4">
        {form.fields.map((field, index) => (
          <div key={index}>
            {(field.type === 'text' || field.type === 'email' || field.type === 'tel') ? (
              <div className="space-y-1">
                <Label>{field.label}{field.required ? ' *' : ''}</Label>
                <Input type={field.type} placeholder={field.placeholder} />
              </div>
            ) : field.type === 'textarea' ? (
              <div className="space-y-1">
                <Label>{field.label}{field.required ? ' *' : ''}</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder={field.placeholder}
                />
              </div>
            ) : field.type === 'select' ? (
              <div className="space-y-1">
                <Label>{field.label}{field.required ? ' *' : ''}</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <option value="">Select...</option>
                  {field.options?.map((option, idx) => (
                    <option key={idx} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            ) : field.type === 'checkbox' ? (
              <div className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300" />
                <Label>{field.label}</Label>
              </div>
            ) : (field.type === 'date' || field.type === 'time' || field.type === 'datetime') ? (
              <div className="space-y-1">
                <Label>{field.label}{field.required ? ' *' : ''}</Label>
                <Input type={field.type === 'datetime' ? 'datetime-local' : field.type} />
              </div>
            ) : field.type === 'file' ? (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{field.label}{field.required ? ' *' : ''}</Label>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <label>
                    Choose File
                    <input type="file" hidden accept={field.validation?.allowed_types?.join(',') || '*'} />
                  </label>
                </Button>
                {field.validation?.max_size_mb && (
                  <p className="text-xs text-muted-foreground">Max size: {field.validation.max_size_mb}MB</p>
                )}
              </div>
            ) : field.type === 'rating' ? (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{field.label}{field.required ? ' *' : ''}</Label>
                <div className="flex gap-0.5">
                  {Array.from({ length: field.validation?.max_stars || 5 }).map((_, i) => (
                    <span key={i} className="text-2xl text-amber-400 cursor-pointer">☆</span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ))}

        {form.show_message_box && (
          <div className="space-y-1">
            <Label>Your Message</Label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="How can we help you today?"
            />
          </div>
        )}

        <Button className="w-full text-white" style={{ backgroundColor: form.primary_color }}>
          Start Conversation
        </Button>
      </CardContent>
    </Card>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 py-4 px-4 md:py-6 lg:px-6">

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Pre-Chat Forms</h1>
            <p className="text-sm text-muted-foreground">
              Collect visitor information before starting a conversation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setSelectedForm(null); setFormDialogOpen(true); }} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Create Form
          </Button>
        </div>
      </div>

      {/* ── KPI Strip ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Total Forms</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{forms.length}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <ClipboardList className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            <span className="text-emerald-600 font-medium">{activeForms} active</span>
            <span className="mx-1">·</span>
            {forms.length - activeForms} disabled
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-emerald-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Active Forms</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{activeForms}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Currently collecting leads
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-amber-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Total Submissions</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{totalSubmissions.toLocaleString()}</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/10">
                <Users className="h-4 w-4 text-amber-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Across all forms
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-violet-500/5 to-card shadow-sm">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardDescription className="text-xs">Avg Conversion</CardDescription>
            <CardTitle className="text-2xl font-bold tabular-nums">{avgConversion}%</CardTitle>
            <CardAction>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/10">
                <TrendingUp className="h-4 w-4 text-violet-500" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="px-4 pb-3 text-xs text-muted-foreground">
            Average across all forms
          </CardFooter>
        </Card>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <Tabs defaultValue="forms" className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="forms" className="gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />
            Forms
            {forms.length > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-medium">
                {forms.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="submissions" className="gap-1.5">
            <BarChart2 className="h-3.5 w-3.5" />
            Submissions
            {submissions.length > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-medium">
                {submissions.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Forms Tab ──────────────────────────────────────────────────────── */}
        <TabsContent value="forms">
          {forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
                <ClipboardList className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="font-semibold mb-1">No pre-chat forms yet</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-xs">
                Create a form to collect visitor information before they start a conversation.
              </p>
              <Button onClick={() => { resetForm(); setSelectedForm(null); setFormDialogOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" /> Create Your First Form
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {forms.map((form) => (
                <Card key={form.id} className="flex flex-col overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card">
                  {/* Status accent bar */}
                  <div className={cn("h-1 w-full shrink-0", form.is_enabled ? "bg-emerald-500" : "bg-muted-foreground/30")} />

                  <CardHeader className="pb-2 pt-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold leading-snug line-clamp-1">
                          {form.title}
                        </CardTitle>
                        <CardDescription className="text-xs mt-0.5 line-clamp-1">
                          {form.subtitle}
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "shrink-0 text-[10px] h-5 px-1.5 gap-1",
                          form.is_enabled
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'border-border bg-muted/40 text-muted-foreground'
                        )}
                      >
                        {form.is_enabled
                          ? <CheckCircle2 className="h-3 w-3" />
                          : <XCircle className="h-3 w-3" />}
                        {form.is_enabled ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 space-y-3 pt-0 pb-3">
                    {/* Stats grid */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { label: 'Fields',      value: String(form.fields.length) },
                        { label: 'Submissions', value: form.total_submissions.toLocaleString() },
                        { label: 'Conversion',  value: `${form.conversion_rate}%` },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-lg bg-muted/50 px-2 py-1.5 text-center">
                          <p className="text-xs font-semibold tabular-nums">{value}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Conversion progress */}
                    {form.total_submissions > 0 && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-[10px] text-muted-foreground">
                          <span>Conversion rate</span>
                          <span>{form.conversion_rate}%</span>
                        </div>
                        <Progress value={form.conversion_rate} className="h-1" />
                      </div>
                    )}

                    {/* Feature flags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {form.is_required && (
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Required</span>
                      )}
                      {form.show_message_box && (
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Message box</span>
                      )}
                      {form.enable_business_hours_check && (
                        <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Biz hours</span>
                      )}
                      {/* Color swatch */}
                      <div className="ml-auto flex items-center gap-1">
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-border/50 shrink-0"
                          style={{ backgroundColor: form.primary_color }}
                          title={form.primary_color}
                        />
                      </div>
                    </div>
                  </CardContent>

                  <Separator />

                  <CardFooter className="flex items-center gap-1 pt-2.5 pb-2.5 px-4">
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                      onClick={() => handleEditForm(form)}>
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                      onClick={() => {
                        setFormData({
                          title: form.title,
                          subtitle: form.subtitle,
                          fields: form.fields,
                          is_enabled: form.is_enabled,
                          is_required: form.is_required,
                          show_message_box: form.show_message_box,
                          primary_color: form.primary_color,
                          enable_business_hours_check: form.enable_business_hours_check,
                          offline_message: form.offline_message,
                        });
                        setPreviewOpen(true);
                      }}>
                      <Eye className="h-3 w-3" /> Preview
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                      onClick={() => fetchSubmissions(form)}>
                      <BarChart2 className="h-3 w-3" /> Data
                    </Button>
                    <Button size="sm" variant="ghost"
                      className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => setFormToDelete(form)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Submissions Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="submissions">
          <Card className="shadow-sm">
            {activeSubmissionFormTitle && (
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Submissions</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Showing data for: <span className="font-medium text-foreground">{activeSubmissionFormTitle}</span>
                    </CardDescription>
                  </div>
                  <Badge variant="secondary">{submissions.length} total</Badge>
                </div>
              </CardHeader>
            )}
            {submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
                  <BarChart2 className="h-8 w-8 text-muted-foreground/60" />
                </div>
                <h3 className="font-semibold mb-1">No submissions loaded</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Click the <strong>Data</strong> button on any form card to load its submissions here.
                </p>
              </div>
            ) : (
              <>
                {activeSubmissionFormTitle && <Separator />}
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Contact</TableHead>
                      <TableHead className="text-xs">Form Data</TableHead>
                      <TableHead className="text-xs">Initial Message</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(sub.submitted_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {sub.form_data.name || sub.form_data.email || (
                            <span className="text-muted-foreground">Anonymous</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(sub.form_data).map(([key, value]) => (
                              <Badge key={key} variant="secondary" className="text-[10px] font-normal">
                                {key}: {String(value)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="text-xs truncate text-muted-foreground">
                            {sub.initial_message || '—'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
                            <MessageSquarePlus className="h-3 w-3" />
                            {sub.conversation_id ? 'View' : 'Create'} Conversation
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Create/Edit Form Dialog ──────────────────────────────────────────── */}
      <Dialog open={formDialogOpen} onOpenChange={setFormDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>
              {selectedForm ? 'Edit Pre-Chat Form' : 'Create Pre-Chat Form'}
            </DialogTitle>
            <DialogDescription>
              Configure appearance, behaviour, and fields. Visitors complete this before starting a conversation.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[min(70vh,calc(90vh-10rem))] flex-1">
            <div className="space-y-6 px-6 py-5">

            {/* Appearance */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Appearance</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="form-title">Form Title</Label>
                  <Input id="form-title" value={formData.title}
                    onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="form-color">Brand Color</Label>
                  <div className="flex items-center gap-2">
                    <input id="form-color" type="color" value={formData.primary_color}
                      onChange={(e) => setFormData((p) => ({ ...p, primary_color: e.target.value }))}
                      className="h-9 w-10 rounded-md border border-input cursor-pointer p-0.5" />
                    <Input value={formData.primary_color}
                      onChange={(e) => setFormData((p) => ({ ...p, primary_color: e.target.value }))}
                      className="font-mono text-sm" />
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-col gap-1.5">
                  <Label htmlFor="form-subtitle">Subtitle</Label>
                  <textarea id="form-subtitle" value={formData.subtitle} rows={2}
                    onChange={(e) => setFormData((p) => ({ ...p, subtitle: e.target.value }))}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Behaviour */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Behaviour</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { key: 'is_enabled',                 label: 'Enable Form',             desc: 'Show form to visitors' },
                  { key: 'is_required',                label: 'Required',                desc: 'Force visitors to fill the form' },
                  { key: 'show_message_box',           label: 'Show Message Box',        desc: 'Allow visitors to send an initial message' },
                  { key: 'enable_business_hours_check',label: 'Business Hours Check',    desc: 'Show offline message outside hours' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <Switch
                      checked={!!(formData as any)[key]}
                      onCheckedChange={(v) => setFormData((p) => ({ ...p, [key]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Fields */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Form Fields</p>

              {/* Quick add predefined fields */}
              {Object.keys(predefinedFields).length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-3">Quick add predefined fields:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(predefinedFields).map((key) => (
                      <Button key={key} size="sm" variant="outline" className="h-7 text-xs gap-1"
                        onClick={() => handleAddPredefinedField(key)}>
                        <Plus className="h-3 w-3" /> {predefinedFields[key].label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom field builder */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                <p className="text-xs font-medium">Add Custom Field</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Input placeholder="Field ID (e.g. company)" value={newField.id}
                    onChange={(e) => setNewField((p) => ({ ...p, id: e.target.value }))} />
                  <select value={newField.type}
                    onChange={(e) => setNewField((p) => ({ ...p, type: e.target.value }))}
                    className={SELECT_CLS}>
                    {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <Input placeholder="Label (e.g. Company)" value={newField.label}
                    onChange={(e) => setNewField((p) => ({ ...p, label: e.target.value }))} />
                  <Button onClick={handleAddField} className="w-full gap-1">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              </div>

              {/* Fields list */}
              {formData.fields.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{formData.fields.length} field{formData.fields.length !== 1 ? 's' : ''} added:</p>
                  {formData.fields.map((field, idx) => (
                    <div key={idx}
                      className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{field.label}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {field.type}{field.required ? ' · Required' : ''}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-[10px] capitalize shrink-0">{field.type}</Badge>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveField(idx)}>
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-6 text-center">
                  <p className="text-xs text-muted-foreground">No fields added yet. Use Quick Add or the form above.</p>
                </div>
              )}
            </div>
            </div>
          </ScrollArea>

          <DialogFooter className="shrink-0 gap-2.5 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => setPreviewOpen(true)} className="gap-1.5">
              <Eye className="h-4 w-4" /> Preview
            </Button>
            <Button onClick={handleSaveForm}>
              {selectedForm ? 'Update' : 'Create'} Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Preview Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>Form Preview</DialogTitle>
            <DialogDescription>How the form appears to visitors before they start a chat.</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-5">
            <PreviewForm form={formData} />
          </div>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ────────────────────────────────────────────────────── */}
      <AlertDialog open={!!formToDelete} onOpenChange={(open) => !open && setFormToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{formToDelete?.title}</strong>? This action cannot be undone and all submission data will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFormToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default PreChatFormsPage;
